import React from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button, Typography, styled } from '@mui/material';
import Add from '@mui/icons-material/Add';
import { AutoFormFields, LegacyForm } from 'lib/LegacyForm';
import { useSortableList } from 'lib/sortable/useSortableList';
import { SortableItemRow, SortableFieldDefinition } from 'lib/sortable/SortableItemRow';
import { Row } from 'ui/Flex';
import { MuiModal } from 'ui/MuiModal';
import { AutoForm } from '../AutoForm';

const PREFIX = 'SortableFormField';

const sortableClasses = {
    root: `${PREFIX}-root`
};

const StyledMuiModal = styled(MuiModal)(({ theme }) => ({
    minWidth: theme.spacing(48)
}));

interface FormComponent<Model> {
    value?: Partial<Model>;
    onSubmit: (value: Partial<Model>) => void;
    close: () => void;
    formTitle: string;
}

interface SortableFieldProps<Model> {
    value?: Model[];
    onChange: (value: Partial<Model>[]) => void;
    fields: SortableFieldDefinition[];
    itemForm: AutoFormFields;
    FormComponent?: React.FunctionComponent<FormComponent<Model>>;
}

export function SortableField<Model extends { index?: number }>(props: SortableFieldProps<Model>) {
    const { value: rawValue, onChange, fields, itemForm, FormComponent } = props;
    const value = rawValue || [];

    const {
        items,
        openEdit,
        openAdd,
        currentItem,
        handleDragEnd,
        handleDeleteItem,
        handleSubmit,
        handleEditItem,
        handleAddItem,
        handleClose
    } = useSortableList<Model>({ value, onChange });

    const renderValue = (item: Model, index: number) => (
        <SortableItemRow
            id={items[index]}
            fields={fields}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            key={items[index]}
            item={item}
            index={index}
        />
    );

    return (
        <React.Fragment>
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={items} strategy={verticalListSortingStrategy}>
                    <Row flex={1} style={{ padding: 8 }}>
                        <div style={{ flexBasis: '5%', marginRight: '30px' }} />
                        {fields &&
                            fields.map(field => (
                                <Typography
                                    sx={{ fontSize: '0.75rem', flexBasis: field.width }}
                                    key={`sortable-title-${field.key}`}
                                >
                                    {field.label}
                                </Typography>
                            ))}
                        <div style={{ flexBasis: '10%' }} />
                    </Row>
                    {value && value.map(renderValue)}
                    <Button variant="outlined" onClick={handleAddItem}>
                        <Add />
                    </Button>
                </SortableContext>
            </DndContext>
            <StyledMuiModal className={sortableClasses.root} open={openEdit} onClose={handleClose}>
                {FormComponent ? (
                    <FormComponent
                        formTitle="Edit item"
                        close={handleClose}
                        value={currentItem}
                        onSubmit={handleSubmit}
                    />
                ) : (
                    <ItemForm
                        itemForm={itemForm}
                        formTitle="Edit item"
                        close={handleClose}
                        value={currentItem}
                        onSubmit={handleSubmit}
                    />
                )}
            </StyledMuiModal>
            <StyledMuiModal className={sortableClasses.root} open={openAdd} onClose={handleClose}>
                {FormComponent ? (
                    <FormComponent
                        formTitle="Add item"
                        close={handleClose}
                        value={currentItem}
                        onSubmit={handleSubmit}
                    />
                ) : (
                    <ItemForm
                        itemForm={itemForm}
                        formTitle="Add item"
                        close={handleClose}
                        onSubmit={handleSubmit}
                    />
                )}
            </StyledMuiModal>
        </React.Fragment>
    );
}

interface ItemFormProps<Model> {
    value?: Partial<Model>;
    onSubmit: (value: Partial<Model>) => void;
    close: () => void;
    formTitle: string;
    itemForm: AutoFormFields;
}

class ItemForm<Model> extends React.PureComponent<ItemFormProps<Model>> {
    form = new LegacyForm(this.props.itemForm, data => {
        this.props.onSubmit(data);
        this.props.close();
    }).update(this.props.value || {});
    render() {
        const { formTitle } = this.props;
        return <AutoForm form={this.form} title={formTitle} />;
    }
}
